import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { getSiteConfig } from '../../lib/api';
import { findSeason, resolveSeason, seasonForDate } from './catalog';
import { SeasonContext, type SeasonState } from './context';
import { LOOKS } from './looks';
import { useRasterArt } from './useRasterArt';
import type { SeasonDefinition } from './types';

// One source of truth: /api/config's `seasonal_theme` (SITE_SEASONAL_THEME,
// else the registry's date windows). Until it answers, the last answer this
// browser saw stands in, and on a first visit the same date windows do, so the
// look is right on the first paint. `?season=<id|look|off>` previews a season
// for the rest of the tab session; `?season=auto` clears the preview.
const PREVIEW_KEY = 'sn.season.preview';
const SERVER_KEY = 'sn.season.server';
const DISMISSED_KEY = 'sn.season.dismissed';
const SERVER_NONE = 'off';
const NO_ART: Record<string, string> = {};

function readPreview(): string | null {
  let query: string | null = null;
  try {
    query = new URLSearchParams(window.location.search).get('season');
  } catch {
    query = null;
  }
  if (query !== null) {
    const v = query.trim().toLowerCase();
    const clear = v === '' || v === 'auto';
    try {
      if (clear) sessionStorage.removeItem(PREVIEW_KEY);
      else sessionStorage.setItem(PREVIEW_KEY, v);
    } catch {
      /* storage blocked: the preview lasts until the next full load */
    }
    return clear ? null : v;
  }
  try {
    return sessionStorage.getItem(PREVIEW_KEY);
  } catch {
    return null;
  }
}

/** undefined = never heard from the server; null = the server says no season. */
function readCachedServer(): string | null | undefined {
  try {
    const v = localStorage.getItem(SERVER_KEY);
    if (v === null) return undefined;
    return v === SERVER_NONE ? null : v;
  } catch {
    return undefined;
  }
}

function writeCachedServer(v: string | null) {
  try {
    localStorage.setItem(SERVER_KEY, v ?? SERVER_NONE);
  } catch {
    /* ignore */
  }
}

function readDismissed(): string | null {
  try {
    return localStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(id: string | null) {
  try {
    if (id) localStorage.setItem(DISMISSED_KEY, id);
    else localStorage.removeItem(DISMISSED_KEY);
  } catch {
    /* ignore */
  }
}

function applySeason(season: SeasonDefinition): () => void {
  const root = document.documentElement;
  root.dataset.season = season.look;
  for (const [name, value] of Object.entries(season.tokens)) root.style.setProperty(name, value);

  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  const priorThemeColor = meta?.content;
  if (meta && season.themeColor) meta.content = season.themeColor;

  // The seasonal display face, fetched ahead of the first heading that uses it.
  let preload: HTMLLinkElement | null = null;
  if (season.fonts?.length) {
    for (const href of season.fonts) {
      preload = document.createElement('link');
      preload.rel = 'preload';
      preload.as = 'font';
      preload.type = 'font/woff2';
      preload.crossOrigin = 'anonymous';
      preload.href = href;
      preload.dataset.season = '';
      document.head.appendChild(preload);
    }
  }

  return () => {
    delete root.dataset.season;
    if (preload) document.head.querySelectorAll('link[rel="preload"][data-season]').forEach(l => l.remove());
    for (const name of Object.keys(season.tokens)) root.style.removeProperty(name);
    if (!root.getAttribute('style')) root.removeAttribute('style');
    if (meta && priorThemeColor !== undefined) meta.content = priorThemeColor;
  };
}

/**
 * Wraps the site Layout: resolves the active season, writes it to
 * `<html data-season>` plus its token overrides, mounts the look's decorative
 * layer, and shares the state with the header and footer. Outside Layout (the
 * OBS overlay) nothing seasonal ever applies.
 */
export function SeasonalTheme({ children }: { children: ReactNode }) {
  const [preview] = useState(readPreview);
  const [server, setServer] = useState<string | null | undefined>(readCachedServer);
  const [dismissed, setDismissed] = useState<string | null>(readDismissed);

  useEffect(() => {
    let alive = true;
    getSiteConfig()
      .then(cfg => {
        // A deployment older than the seasonal field leaves the guess alone.
        if (!alive || !('seasonal_theme' in cfg)) return;
        const v = cfg.seasonal_theme ?? null;
        setServer(v);
        writeCachedServer(v);
      })
      .catch(() => {
        /* no config (vite dev, offline): the date-window guess stands */
      });
    return () => {
      alive = false;
    };
  }, []);

  const available = useMemo<SeasonDefinition | null>(() => {
    if (preview) return resolveSeason(preview);
    if (server !== undefined) return server ? findSeason(server) : null;
    return seasonForDate();
  }, [preview, server]);

  const effectsOn = !(available && dismissed === available.id);
  const season = effectsOn ? available : null;

  // Layout effect: the attribute and tokens land before the first paint.
  useLayoutEffect(() => {
    if (!season) return;
    return applySeason(season);
  }, [season]);

  // Ambient motion stops on a hidden tab rather than ticking in the background.
  useEffect(() => {
    if (!season) return;
    const root = document.documentElement;
    const sync = () => {
      if (document.hidden) root.dataset.seasonPaused = '';
      else delete root.dataset.seasonPaused;
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      delete root.dataset.seasonPaused;
    };
  }, [season]);

  const setEffectsOn = useCallback(
    (on: boolean) => {
      const id = on ? null : (available?.id ?? null);
      writeDismissed(id);
      setDismissed(id);
    },
    [available],
  );

  // Raster pieces load on demand: a piece is fetched only while something that
  // draws it is mounted (useSeasonArt registers it, reference-counted), so a
  // page that shows none of the scenery downloads none of it. Each requested
  // piece whose file exists is listed on <html> and exposed as a CSS url()
  // variable, so stylesheets can swap it in under `[data-season-art~="<piece>"]`
  // and keep their fallback otherwise.
  const counts = useRef(new Map<string, number>());
  const [active, setActive] = useState<string>('');
  const syncActive = useCallback(() => {
    const next = [...counts.current.entries()]
      .filter(([, n]) => n > 0)
      .map(([piece]) => piece)
      .sort()
      .join(' ');
    setActive(prev => (prev === next ? prev : next));
  }, []);
  const requestArt = useCallback(
    (piece: string) => {
      counts.current.set(piece, (counts.current.get(piece) ?? 0) + 1);
      syncActive();
      return () => {
        counts.current.set(piece, (counts.current.get(piece) ?? 1) - 1);
        syncActive();
      };
    },
    [syncActive],
  );
  const wanted = useMemo(() => {
    if (!season?.art || !active) return undefined;
    const pick: Record<string, string> = {};
    for (const piece of active.split(' ')) if (season.art[piece]) pick[piece] = season.art[piece];
    return pick;
  }, [season, active]);
  const loadedArt = useRasterArt(wanted);
  const art = season ? loadedArt : NO_ART;

  useLayoutEffect(() => {
    const pieces = Object.keys(art);
    if (pieces.length === 0) return;
    const root = document.documentElement;
    root.dataset.seasonArt = pieces.join(' ');
    for (const [piece, url] of Object.entries(art)) root.style.setProperty(`--season-art-${piece}`, `url("${url}")`);

    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const priorIcon = icon?.getAttribute('href') ?? null;
    if (icon && art.favicon) icon.href = art.favicon;

    return () => {
      delete root.dataset.seasonArt;
      for (const piece of pieces) root.style.removeProperty(`--season-art-${piece}`);
      if (!root.getAttribute('style')) root.removeAttribute('style');
      if (icon && art.favicon && priorIcon !== null) icon.setAttribute('href', priorIcon);
    };
  }, [art]);

  const { pathname } = useLocation();
  const quiet = !!season?.quietRoutes?.some(path => matchPath({ path, end: false }, pathname));

  // Pieces owned by the shell rather than a component: the tab icon always,
  // the footer's ironwork except on a quiet route (whose page brings its own
  // scenery).
  useEffect(() => {
    if (!season?.art?.favicon) return;
    return requestArt('favicon');
  }, [season, requestArt]);
  useEffect(() => {
    if (!season?.art?.ironwork || quiet) return;
    return requestArt('ironwork');
  }, [season, quiet, requestArt]);

  const state = useMemo<SeasonState>(
    () => ({ season, available, effectsOn, setEffectsOn, art, requestArt }),
    [season, available, effectsOn, setEffectsOn, art, requestArt],
  );

  // Display face for short headings only: mark the qualifying elements before
  // paint on every route, and again whenever the page swaps content in (a
  // title that arrives with its data). Observer callbacks run before the next
  // paint, so a heading never shows in one face and then the other.
  useLayoutEffect(() => {
    const rule = season?.display;
    if (!rule) return;
    const ATTR = 'data-season-display';
    const mark = () => {
      document.querySelectorAll<HTMLElement>(rule.selector).forEach(el => {
        const short = (el.textContent ?? '').trim().length <= rule.maxChars;
        if (short !== el.hasAttribute(ATTR)) el.toggleAttribute(ATTR, short);
      });
    };
    mark();
    const observer = new MutationObserver(mark);
    observer.observe(document.querySelector('main') ?? document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return () => {
      observer.disconnect();
      document.querySelectorAll(`[${ATTR}]`).forEach(el => el.removeAttribute(ATTR));
    };
  }, [season, pathname]);

  const Layer = season && !quiet ? LOOKS[season.look]?.Layer : undefined;

  return (
    <SeasonContext value={state}>
      {Layer && season && <Layer season={season} />}
      {children}
    </SeasonContext>
  );
}
